import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, errorText } from '../api';
import { fehlendeFunktion, upgradeAnbieten } from '../billtoro/upgrade';
import { ConfirmDialog, Dialog } from '../components/Dialog';
import { useToast } from '../lib/toast';
import { isBilltoro } from '../produkt';
import { deQty, dePrice, eurPrice, parsePrice, RATE_LABEL, rateAus, UNITS } from './formular';
import { LAGER_SATZ, mengeText, zaehlungUrl } from './lager/LagerDaten';
import { einArtikelUrl, gleicheLeistung, leistungAus, useLeistungen, type Leistung, type LeistungBody } from './LeistungDaten';
import { darf, fehlerText, NurAnsehen, TarifMarke, tarifFehler } from './tarifHinweis';
import type { Overview, Rate } from './types';
import './leistungen.css';

const BILLTORO = isBilltoro();

const RATES: Rate[] = [19, 7, 0];
const RATE_LANG: Record<string, string> = {
  '19': '19 %',
  '7': '7 %',
  '0': '0 %',
};

interface Werte {
  name: string;
  description: string;
  unit: string;
  price: string;
  rate: Rate;
  // Lager (nur Billtoro): Artikelnummer, „Bestand führen“, Bestand beim Einschalten (bucht eine Zählung), Mindestbestand
  artikelnummer: string;
  lager: boolean;
  bestandJetzt: string;
  mindest: string;
}
const LEER: Werte = { name: '', description: '', unit: '', price: '', rate: 19, artikelnummer: '', lager: false, bestandJetzt: '', mindest: '' };
// Menge ≥ 0 in der Einheit (bis 3 Nachkommastellen): leer → null, ungültig → undefined
function mengeAus(text: string): number | null | undefined {
  const t = text.trim().replace(',', '.');
  if (!t) return null;
  return /^\d+(\.\d{1,3})?$/.test(t) && Number(t) < 1e6 ? Number(t) : undefined;
}

// Leistungen und Artikel (/rechnung/leistungen): was die Firma oft berechnet, mit Preis netto (bis 4 Stellen), Einheit
// und Umsatzsteuer. Beim Schreiben einer Rechnung schlägt das Feld „Leistung“ sie vor. Darunter Vorschläge aus den
// eigenen Rechnungen (noch nicht gespeicherte Positionen) zum Übernehmen mit einem Klick.
// Billtoro: Artikelnummer und „Bestand führen“ (Lager, einschalten nur mit Lager im Tarif, ausschalten immer); beim
// Einschalten bucht „Bestand jetzt“ nach dem Speichern eine Zählung „Anfangsbestand“.
export function Leistungen({ data }: { data: Overview; reload(): Promise<void> }) {
  const toast = useToast();
  const ku = data.absender.kleinunternehmer;
  // Billtoro: anlegen, ändern und löschen mit dem Recht „Kontakte, Leistungen und Lager“
  const pflegen = darf(data.tarif, 'kunden');
  const leistungen = useLeistungen(data.firm.id);
  const { list, loaded } = leistungen;
  const [q, setQ] = useState('');
  // Formular: null zu, 'neu' oder die Leistung, die geändert wird
  const [edit, setEdit] = useState<Leistung | 'neu' | null>(null);
  const [w, setW] = useState<Werte>(LEER);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [weg, setWeg] = useState<Leistung | null>(null);

  const t = q.trim().toLowerCase();
  const shown = t ? list.filter((l) => `${l.name} ${l.description ?? ''} ${l.artikelnummer ?? ''}`.toLowerCase().includes(t)) : list;
  const lagerFehlt = fehlendeFunktion(data.tarif, 'lager');
  // Leistung, die schon Bestand führt (dann kein „Bestand jetzt“, der Bestand steht im Lager)
  const schonImLager = edit !== null && edit !== 'neu' && !!edit.lager;

  // Positionen aus den eigenen Rechnungen, die noch keine Leistung sind: je Text der neueste Preis, häufige zuerst
  const vorschlaege = useMemo(() => {
    const m = new Map<string, { name: string; unit: string; price: number; rate: Rate; n: number }>();
    for (const r of data.verlauf) {
      for (const i of r.items) {
        const name = i.description.trim();
        if (name.length < 2 || name.length > 120 || !(i.price > 0)) continue;
        const k = name.toLowerCase();
        const v = m.get(k);
        // data.verlauf: neueste Rechnung zuerst
        if (v) v.n += 1;
        else m.set(k, { name, unit: i.unit, price: i.price, rate: rateAus(i.rate) ?? 19, n: 1 });
      }
    }
    return [...m.values()]
      .filter((v) => !gleicheLeistung(list, v.name))
      .sort((a, b) => b.n - a.n)
      .slice(0, 6);
  }, [data.verlauf, list]);

  function oeffnen(l: Leistung | 'neu') {
    setEdit(l);
    setError(null);
    setW(
      l === 'neu'
        ? LEER
        : {
            name: l.name,
            description: l.description ?? '',
            unit: l.unit,
            price: l.price != null ? dePrice(l.price) : '',
            rate: l.rate,
            artikelnummer: l.artikelnummer ?? '',
            lager: !!l.lager,
            bestandJetzt: '',
            mindest: l.mindest != null ? deQty(l.mindest) : '',
          },
    );
  }

  function body(v: Werte): LeistungBody | null {
    const price = parsePrice(v.price);
    if (price === undefined) {
      setError('Bitte den Preis netto prüfen, z. B. 45,00 oder 0,085 (höchstens 4 Stellen nach dem Komma).');
      return null;
    }
    const b: LeistungBody = { name: v.name, description: v.description.trim() || null, unit: v.unit, price, rate: ku ? 19 : v.rate };
    if (!BILLTORO) return b;
    const mindest = mengeAus(v.mindest);
    if (v.lager && !v.unit.trim()) {
      setError('Bitte eine Einheit eintragen, z. B. Stück.');
      return null;
    }
    if (v.lager && (mindest === undefined || mengeAus(v.bestandJetzt) === undefined)) {
      setError(mindest === undefined ? 'Bitte den Mindestbestand prüfen, z. B. 5 oder 2,5.' : 'Bitte den Bestand prüfen, z. B. 12 oder 2,5.');
      return null;
    }
    return { ...b, artikelnummer: v.artikelnummer.trim() || null, lager: v.lager, mindest: v.lager ? (mindest ?? null) : null };
  }

  // „Bestand jetzt“ beim Einschalten: Zählung „Anfangsbestand“ mit dem heutigen Datum; scheitert nur sie, bleibt die
  // Leistung gespeichert
  async function anfangsbestand(l: Leistung, text: string) {
    const menge = mengeAus(text);
    if (!l.lager || menge == null) return l;
    try {
      await api.post(zaehlungUrl(data.firm.id), { datum: data.today, notiz: 'Anfangsbestand', zeilen: [{ artikelId: l.id, gezaehlt: menge }] });
      return { ...l, bestand: menge };
    } catch (err) {
      if (!tarifFehler(err)) toast(`Leistung gespeichert, Bestand nicht: ${fehlerText(err) ?? ''}`);
      return l;
    }
  }

  async function speichern(e: FormEvent) {
    e.preventDefault();
    if (!edit) return;
    setError(null);
    const b = body(w);
    if (!b) return;
    setBusy(true);
    try {
      // „Bestand jetzt“ nur beim Einschalten des Hakens
      const jetzt = BILLTORO && w.lager && !schonImLager ? w.bestandJetzt : '';
      if (edit === 'neu') {
        const neu = await leistungen.add(b);
        toast(`Leistung „${b.name.trim()}“ gespeichert`);
        const mit = await anfangsbestand(neu, jetzt);
        if (mit !== neu) leistungen.setList((cur) => cur.map((x) => (x.id === mit.id ? mit : x)));
      } else {
        const l = await anfangsbestand(leistungAus(await api.put<Leistung>(einArtikelUrl(edit.id), b)), jetzt);
        leistungen.setList((cur) => cur.map((x) => (x.id === l.id ? l : x)).sort((a, x) => a.name.localeCompare(x.name, 'de')));
        toast('Leistung geändert');
      }
      setEdit(null);
    } catch (err) {
      // Tarif: das Fenster „Mehr mit Billtoro“ zeigt es (dieses schließen, es kann nur eines offen sein)
      if (tarifFehler(err)) setEdit(null);
      else setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function uebernehmen(v: (typeof vorschlaege)[number]) {
    try {
      await leistungen.add({ name: v.name, description: null, unit: v.unit, price: v.price, rate: ku ? 19 : v.rate });
      toast(`Leistung „${v.name}“ gespeichert`);
    } catch (err) {
      toast(errorText(err));
    }
  }

  async function loeschen() {
    if (!weg) return;
    setBusy(true);
    try {
      await api.del(einArtikelUrl(weg.id));
      leistungen.setList((cur) => cur.filter((x) => x.id !== weg.id));
      toast('Leistung gelöscht');
      setWeg(null);
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const preisText = (l: Pick<Leistung, 'price' | 'unit'>) =>
    l.price != null ? `${eurPrice(l.price)}${l.unit ? ` je ${l.unit}` : ''}` : `ohne festen Preis${l.unit ? ` · ${l.unit}` : ''}`;

  return (
    <>
      <div className="rt-head">
        <div>
          <h1>
            Leistungen und Artikel
            <NurAnsehen tarif={data.tarif} bereich="kunden" />
          </h1>
          <p className="muted">
            {list.length
              ? `${list.length} ${list.length === 1 ? 'Leistung' : 'Leistungen'} · werden beim Schreiben einer Rechnung vorgeschlagen`
              : 'Was Sie oft berechnen, mit Preis und Einheit'}
          </p>
        </div>
        {(pflegen || BILLTORO) && (
          <div className="rt-head-act">
            {BILLTORO && (
              <Link to="/rechnung/lager" className="btn">
                Zum Lager
              </Link>
            )}
            {pflegen && (
              <button type="button" className="btn primary" onClick={() => oeffnen('neu')}>
                + Neue Leistung
              </button>
            )}
          </div>
        )}
      </div>

      {list.length > 6 && (
        <div className="rt-tools">
          <input
            type="search"
            className="rt-search"
            value={q}
            placeholder={BILLTORO ? 'Suchen: Name, Beschreibung oder Artikelnummer' : 'Suchen: Name oder Beschreibung'}
            aria-label="Leistungen suchen"
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      )}

      {!loaded ? (
        <p className="muted">Lädt …</p>
      ) : list.length === 0 ? (
        <div className="rt-card rt-empty">
          <p>
            Noch keine Leistungen. Speichern Sie, was Sie oft berechnen (z. B. Unterhaltsreinigung je m² oder Arbeitsstunde): Beim
            Schreiben einer Rechnung tippen Sie die ersten Buchstaben, Text, Preis und Steuer kommen von selbst.
          </p>
          {pflegen && (
            <button type="button" className="btn primary" onClick={() => oeffnen('neu')}>
              Erste Leistung anlegen
            </button>
          )}
        </div>
      ) : shown.length === 0 ? (
        <div className="rt-card rt-empty">
          <p>Keine Leistung passt zu „{q}“.</p>
          <button type="button" className="btn small" onClick={() => setQ('')}>
            Alle zeigen
          </button>
        </div>
      ) : (
        <ul className={`ls-list${list.some((l) => l.lager) ? ' mit-lager' : ''}`}>
          {shown.map((l) => (
            <li key={l.id} className="ls-row">
              <span className="ls-t">
                <b>
                  {l.artikelnummer && <span className="ls-nr num">{l.artikelnummer}</span>}
                  {l.name}
                </b>
                {l.description && <span className="muted">{l.description}</span>}
              </span>
              <span className="ls-bestand">
                {l.lager && l.bestand != null && (
                  <Link to={`/rechnung/lager/${l.id}`} className={l.bestand < 0 ? 'bad' : l.mindest != null && l.bestand < l.mindest ? 'warn' : undefined}>
                    Bestand <b className="num">{mengeText(l.bestand, l.unit)}</b>
                  </Link>
                )}
              </span>
              <span className="ls-p">
                <span className="num">{preisText(l)}</span>
                {!ku && <small className="muted">{RATE_LABEL[String(l.rate)]}</small>}
              </span>
              {pflegen && (
                <span className="ls-act">
                  <button type="button" className="linkbtn" aria-label={`${l.name} ändern`} onClick={() => oeffnen(l)}>
                    Ändern
                  </button>
                  <button type="button" className="linkbtn rt-danger" aria-label={`${l.name} löschen`} onClick={() => setWeg(l)}>
                    Löschen
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {loaded && pflegen && !t && vorschlaege.length > 0 && (
        <section className="ls-vor" aria-label="Aus Ihren Rechnungen">
          <h2>Aus Ihren Rechnungen</h2>
          <p className="muted">Diese Positionen haben Sie schon berechnet. Mit einem Klick als Leistung speichern:</p>
          <ul>
            {vorschlaege.map((v) => (
              <li key={v.name}>
                <span className="ls-t">
                  <b>{v.name}</b>
                  <span className="muted">
                    {preisText(v)}
                    {ku ? '' : ` · ${RATE_LABEL[String(v.rate)]}`}
                  </span>
                </span>
                <button type="button" className="btn small" aria-label={`${v.name} als Leistung speichern`} onClick={() => void uebernehmen(v)}>
                  Speichern
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="ls-foot muted">
        Geschriebene Rechnungen bleiben, wie sie sind, auch wenn Sie hier einen Preis ändern.
        {darf(data.tarif, 'rechnungen') && (
          <>
            {' '}
            <Link to="/rechnung/neu">Neue Rechnung schreiben</Link>
          </>
        )}
      </p>

      <Dialog open={!!edit} onClose={() => setEdit(null)} title={edit === 'neu' ? 'Neue Leistung' : 'Leistung ändern'} className="ls-dlg">
        <form onSubmit={(e) => void speichern(e)} noValidate>
          <label className="field">
            <span>Name</span>
            <input value={w.name} maxLength={120} placeholder="z. B. Unterhaltsreinigung" onChange={(e) => setW({ ...w, name: e.target.value })} />
            <small>Steht so als Leistung auf der Rechnung.</small>
          </label>
          <label className="field">
            <span>Beschreibung (optional)</span>
            <input
              value={w.description}
              maxLength={170}
              placeholder="z. B. Büroflächen inkl. Sanitär"
              onChange={(e) => setW({ ...w, description: e.target.value })}
            />
            <small>Kommt mit auf die Rechnung: „Name – Beschreibung“.</small>
          </label>
          <div className="ls-grid">
            <label className="field">
              <span>Preis netto (optional)</span>
              <input value={w.price} inputMode="decimal" placeholder="z. B. 0,085" onChange={(e) => setW({ ...w, price: e.target.value })} />
            </label>
            <label className="field">
              <span>Einheit</span>
              <input value={w.unit} maxLength={20} list="ls-units" placeholder="z. B. m²" onChange={(e) => setW({ ...w, unit: e.target.value })} />
              <datalist id="ls-units">
                {UNITS.map((u) => (
                  <option key={u} value={u} />
                ))}
              </datalist>
            </label>
          </div>
          {BILLTORO && (
            <div className="ls-lager">
              <label className="field">
                <span>Artikelnummer (optional)</span>
                <input
                  value={w.artikelnummer}
                  maxLength={40}
                  placeholder="z. B. SM6"
                  onChange={(e) => setW({ ...w, artikelnummer: e.target.value })}
                />
                <small>Steht beim Übernehmen in eine Position vorn im Text.</small>
              </label>
              <label className="ls-haken">
                <input
                  type="checkbox"
                  checked={w.lager}
                  onChange={(e) => {
                    // einschalten nur mit Lager im Tarif (sonst „Mehr mit Billtoro“; dieses Fenster vorher schließen)
                    if (e.target.checked && lagerFehlt && !schonImLager) {
                      setEdit(null);
                      upgradeAnbieten(data.tarif, 'lager');
                      return;
                    }
                    setW({ ...w, lager: e.target.checked });
                  }}
                />
                <span>
                  Bestand führen (Ware im Lager)
                  <TarifMarke tarif={data.tarif} fehlt={w.lager || schonImLager ? null : lagerFehlt} />
                  <small className="muted">{LAGER_SATZ}</small>
                </span>
              </label>
              {w.lager && (
                <div className="ls-grid">
                  {schonImLager ? (
                    <p className="ls-jetzt">
                      Bestand{' '}
                      <Link to={`/rechnung/lager/${(edit as Leistung).id}`}>
                        <b className="num">{mengeText((edit as Leistung).bestand ?? 0, (edit as Leistung).unit)}</b>
                      </Link>
                    </p>
                  ) : (
                    <label className="field">
                      <span>Bestand jetzt</span>
                      <input
                        value={w.bestandJetzt}
                        inputMode="decimal"
                        placeholder="0"
                        onChange={(e) => setW({ ...w, bestandJetzt: e.target.value })}
                      />
                      <small>In der Einheit{w.unit.trim() ? ` (${w.unit.trim()})` : ''}. Leer: 0.</small>
                    </label>
                  )}
                  <label className="field">
                    <span>Mindestbestand (optional)</span>
                    <input value={w.mindest} inputMode="decimal" placeholder="z. B. 5" onChange={(e) => setW({ ...w, mindest: e.target.value })} />
                    <small>Darunter steht der Artikel unter „Wenig Bestand“.</small>
                  </label>
                </div>
              )}
            </div>
          )}
          {!ku && (
            <label className="field">
              <span>Umsatzsteuer</span>
              <select value={String(w.rate)} onChange={(e) => setW({ ...w, rate: RATES.find((r) => String(r) === e.target.value) ?? 19 })}>
                {RATES.map((r) => (
                  <option key={r} value={String(r)}>
                    {RATE_LANG[String(r)]}
                  </option>
                ))}
              </select>
              <small>Passt die Steuer nicht zum Kunden (z. B. RC EU bei Kunden in Deutschland), bleibt in der Rechnung die bisherige.</small>
            </label>
          )}
          {error && <div className="notice bad">{error}</div>}
          <div className="dlg-actions">
            <button type="button" className="btn" onClick={() => setEdit(null)}>
              Abbrechen
            </button>
            <button type="submit" className="btn primary" disabled={busy}>
              Speichern
            </button>
          </div>
        </form>
      </Dialog>
      <ConfirmDialog
        open={!!weg}
        title={`Leistung „${weg?.name ?? ''}“ löschen?`}
        text={
          weg?.lager
            ? `„${weg.name}“ wird aus der Liste entfernt. Der Verlauf im Lager bleibt gespeichert. Geschriebene Rechnungen bleiben, wie sie sind.`
            : 'Sie wird nicht mehr vorgeschlagen. Geschriebene Rechnungen bleiben, wie sie sind.'
        }
        confirmLabel="Ja, löschen"
        danger
        busy={busy}
        onConfirm={() => void loeschen()}
        onCancel={() => setWeg(null)}
      />
    </>
  );
}
